(() => {
  if (customElements.get('purefect-nutrition-facts')) return;

  const NUTRIENTS = [
    'total_fat_g',
    'saturated_fat_g',
    'trans_fat_g',
    'cholesterol_mg',
    'sodium_mg',
    'total_carbohydrate_g',
    'dietary_fiber_g',
    'total_sugars_g',
    'added_sugars_g',
    'protein_g',
    'vitamin_d_mcg',
    'calcium_mg',
    'iron_mg',
    'potassium_mg',
  ];

  const CORE_ADDITIVE_NUTRIENTS = ['calories', 'total_fat_g', 'sodium_mg', 'total_carbohydrate_g', 'protein_g'];
  const OPTIONAL_MICRONUTRIENTS = ['vitamin_d_mcg', 'calcium_mg', 'iron_mg', 'potassium_mg'];
  const PALEO_COMPATIBLE_SIDES = new Set([]);

  const DAILY_VALUES = {
    total_fat_g: 78,
    saturated_fat_g: 20,
    cholesterol_mg: 300,
    sodium_mg: 2300,
    total_carbohydrate_g: 275,
    dietary_fiber_g: 28,
    added_sugars_g: 50,
    vitamin_d_mcg: 20,
    calcium_mg: 1300,
    iron_mg: 18,
    potassium_mg: 4700,
  };

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatAmount = (value) => {
    if (!Number.isFinite(value)) return '—';
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
  };

  const emptyNutritionData = () => ({
    base: {},
    variants: {},
    currentVariantId: '',
    sides: {},
    nutritionViews: {},
    lifestyle: {},
  });

  class PurefectNutritionFacts extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedAndReady) return;
      this.isConnectedAndReady = true;
      this.abortController = new AbortController();
      this.data = this.readData();
      this.currentNutritionViewId = this.resolveNutritionViewId(this.data.nutritionViews?.default);

      document.addEventListener('change', this.handleChange.bind(this), {
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:section:load', this.handleSectionLoad.bind(this), {
        signal: this.abortController.signal,
      });
      document.addEventListener(
        'pm:finishing-touches:change',
        this.handleFinishingTouchesChange.bind(this),
        { signal: this.abortController.signal }
      );
      this.addEventListener('click', this.handleViewClick.bind(this), {
        signal: this.abortController.signal,
      });
      this.addEventListener('keydown', this.handleViewKeydown.bind(this), {
        signal: this.abortController.signal,
      });

      this.render(false);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.isConnectedAndReady = false;
    }

    readData() {
      const source = this.querySelector('[data-pm-nutrition-data]');
      if (!source) return emptyNutritionData();

      try {
        const parsed = JSON.parse(source.textContent);
        return {
          base: parsed.base || {},
          variants: parsed.variants || {},
          currentVariantId: String(parsed.currentVariantId || ''),
          sides: parsed.sides || {},
          nutritionViews: parsed.nutritionViews || {},
          lifestyle: parsed.lifestyle || {},
        };
      } catch (error) {
        console.warn('Purefect nutrition data could not be read.', error);
        return emptyNutritionData();
      }
    }

    get currentProfile() {
      return this.data.variants[String(this.data.currentVariantId)] || this.data.base || {};
    }

    get nutritionViewItems() {
      const views = this.data.nutritionViews?.views;
      if (!Array.isArray(views)) return [];

      return views.filter((view) => view && typeof view === 'object' && String(view.id || ''));
    }

    resolveNutritionViewId(requestedId) {
      const views = this.nutritionViewItems;
      if (!views.length) return '';

      const requested = String(requestedId || '');
      const matchingView = views.find((view) => String(view.id) === requested);
      if (matchingView) return String(matchingView.id);

      const completeView = views.find((view) => String(view.id) === 'complete');
      return String((completeView || views[0]).id);
    }

    get currentNutritionView() {
      return (
        this.nutritionViewItems.find(
          (view) => String(view.id) === String(this.currentNutritionViewId)
        ) || null
      );
    }

    componentEntriesForView(view) {
      const rawKeys = Array.isArray(view?.component_keys) ? view.component_keys : [];
      const keys = [...new Set(rawKeys.map((key) => String(key || '')).filter(Boolean))];
      const components = this.data.nutritionViews?.components || {};

      return keys.map((key) => {
        const profile = components[key] || null;
        return {
          key,
          profile,
          label: profile?.label || key.replace(/-/g, ' '),
        };
      });
    }

    get currentViewComponentEntries() {
      return this.componentEntriesForView(this.currentNutritionView);
    }

    get scopeRoot() {
      const containingProduct = this.closest('product-info');
      if (containingProduct) return containingProduct;

      return (
        Array.from(document.querySelectorAll('product-info')).find(
          (productInfo) =>
            String(productInfo.dataset.productId) === String(this.dataset.productId) &&
            !productInfo.dataset.originalSection
        ) || document
      );
    }

    get pickerGroup() {
      return Array.from(this.scopeRoot.querySelectorAll('[data-pm-side-pickers]')).find(
        (group) => String(group.dataset.productId) === String(this.dataset.productId)
      );
    }

    get selectedSideKeys() {
      const group = this.pickerGroup;
      if (!group) return [];

      return Array.from(group.querySelectorAll('input[data-pm-real-side="true"]:checked'))
        .map((input) => input.dataset.pmNutritionKey)
        .filter(Boolean);
    }

    get viewTabs() {
      return Array.from(this.querySelectorAll('[role="tab"][data-nutrition-view]'));
    }

    selectNutritionView(viewId, announce = true) {
      const resolvedId = this.resolveNutritionViewId(viewId);
      if (!resolvedId || resolvedId === this.currentNutritionViewId) {
        this.updateViewTabs();
        return;
      }

      this.currentNutritionViewId = resolvedId;
      this.render(announce);
    }

    handleViewClick(event) {
      const tab = event.target.closest?.('[role="tab"][data-nutrition-view]');
      if (!tab || !this.contains(tab)) return;
      this.selectNutritionView(tab.dataset.nutritionView, true);
    }

    handleViewKeydown(event) {
      const tab = event.target.closest?.('[role="tab"][data-nutrition-view]');
      if (!tab || !this.contains(tab) || event.altKey || event.ctrlKey || event.metaKey) return;

      const tabs = this.viewTabs;
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex < 0) return;

      let nextIndex;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextTab = tabs[nextIndex];
      this.selectNutritionView(nextTab.dataset.nutritionView, true);
      nextTab.focus();
    }

    updateViewTabs() {
      const tabs = this.viewTabs;
      let activeTab = null;

      tabs.forEach((tab) => {
        const isActive = String(tab.dataset.nutritionView) === String(this.currentNutritionViewId);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        if (isActive) activeTab = tab;
      });

      const panel = this.querySelector('[data-nutrition-view-panel]');
      if (panel && activeTab?.id) panel.setAttribute('aria-labelledby', activeTab.id);
    }

    handleChange(event) {
      const variantInput = event.target.closest?.('input[name="id"].product-variant-id');
      if (variantInput?.value) {
        if (this.scopeRoot !== document && !this.scopeRoot.contains(variantInput)) return;
        const sourceProduct = variantInput.closest('product-info');
        if (
          sourceProduct &&
          String(sourceProduct.dataset.productId) !== String(this.dataset.productId)
        ) {
          return;
        }
        this.data.currentVariantId = String(variantInput.value);
        this.render(true);
        return;
      }

      const input = event.target.closest?.('input[data-pm-real-side="true"]');
      if (!input) return;
      if (this.scopeRoot !== document && !this.scopeRoot.contains(input)) return;
      if (String(input.closest('[data-pm-side-pickers]')?.dataset.productId) !== String(this.dataset.productId)) {
        return;
      }

      this.render(true);
    }

    handleSectionLoad() {
      window.requestAnimationFrame(() => this.render(false));
    }

    handleFinishingTouchesChange(event) {
      const source = event.target?.closest?.('[data-pm-finishing-touches]');
      if (!source || (this.scopeRoot !== document && !this.scopeRoot.contains(source))) return;
      if (String(source.dataset.productId) !== String(this.dataset.productId)) return;

      const hasSelections = Number(event.detail?.selections?.length || 0) > 0;
      this.querySelectorAll('[data-nutrition-extras-note], [data-nutrition-extras-summary]').forEach((note) => {
        note.hidden = !hasSelections;
      });
    }

    totalFor(key, profiles) {
      let total = toNumber(this.currentProfile[key]);
      if (total === null) return null;

      for (const profile of profiles) {
        const amount = toNumber(profile?.[key]);
        if (amount === null) return null;
        total += amount;
      }

      return total;
    }

    updateAmount(key, value) {
      const amount = this.querySelector(`[data-nutrition-value="${key}"]`);
      const quickAmounts = this.querySelectorAll(`[data-nutrition-quick="${key}"]`);
      const unit = this.querySelector(`[data-nutrition-unit-for="${key}"]`);
      const dailyValue = this.querySelector(`[data-nutrition-dv="${key}"]`);
      const row = this.querySelector(`[data-nutrition-row="${key}"]`);

      if (row && OPTIONAL_MICRONUTRIENTS.includes(key)) {
        row.hidden = !Number.isFinite(value);
      }

      if (amount) amount.textContent = formatAmount(value);
      quickAmounts.forEach((quickAmount) => {
        quickAmount.textContent = formatAmount(value);
      });
      if (unit) unit.hidden = !Number.isFinite(value);
      if (dailyValue) {
        const reference = DAILY_VALUES[key];
        const percent = Number.isFinite(value) && reference ? Math.round((value / reference) * 100) : null;
        dailyValue.textContent = percent === null ? '' : `${percent}%`;
        if (percent === null) {
          dailyValue.removeAttribute('aria-label');
        } else {
          dailyValue.setAttribute('aria-label', `${percent} percent Daily Value`);
        }
      }
    }

    updateCartSnapshot(key, value) {
      const input = this.querySelector(`[data-pm-cart-snapshot="${key}"]`);
      if (!input) return;
      input.value = Number.isFinite(value) ? formatAmount(value) : String(value || '');
    }

    lifestyleForBuild({ requiredSides, keys, totalCarbs, dietaryFiber }) {
      const lifestyle = this.data.lifestyle || {};
      const selectionComplete = requiredSides === 0 || keys.length >= requiredSides;
      const netCarbs = Number.isFinite(totalCarbs) && Number.isFinite(dietaryFiber)
        ? Math.max(0, totalCarbs - dietaryFiber)
        : null;
      const chips = [];
      const finalLabels = [];

      if (lifestyle.keto || lifestyle.ketoOption) {
        const ketoStillFits = Number.isFinite(netCarbs) ? netCarbs <= 10 : lifestyle.keto && keys.length === 0;
        if (ketoStillFits && selectionComplete) {
          chips.push({ label: 'Keto-Friendly', state: 'confirmed' });
          finalLabels.push('Keto-Friendly');
        } else if (ketoStillFits && !selectionComplete) {
          chips.push({ label: 'Keto option', state: 'possible' });
        }
      }

      if (lifestyle.paleo || lifestyle.paleoOption) {
        const sidesStillFit = keys.every((key) => PALEO_COMPATIBLE_SIDES.has(key));
        if (sidesStillFit && selectionComplete) {
          chips.push({ label: 'Paleo', state: 'confirmed' });
          finalLabels.push('Paleo');
        } else if (sidesStillFit && !selectionComplete) {
          chips.push({ label: 'Paleo option', state: 'possible' });
        }
      }

      return { chips, finalLabels, netCarbs, selectionComplete };
    }

    renderLifestyle(result) {
      const wrapper = this.querySelector('[data-nutrition-lifestyle]');
      const list = this.querySelector('[data-nutrition-lifestyle-list]');
      if (!wrapper || !list) return;

      list.replaceChildren(
        ...result.chips.map((chip) => {
          const item = document.createElement('li');
          item.className = `pm-nutrition-facts__lifestyle-chip pm-nutrition-facts__lifestyle-chip--${chip.state}`;
          item.textContent = chip.label;
          return item;
        })
      );
      wrapper.hidden = result.chips.length === 0;
    }

    render(announce) {
      const base = this.currentProfile;
      const nutritionView = this.currentNutritionView;
      const componentEntries = this.currentViewComponentEntries;
      const componentProfiles = componentEntries.map((entry) => entry.profile);
      const componentNames = componentEntries.map((entry) => entry.label);
      const keys = this.selectedSideKeys;
      const sideProfiles = keys.map((key) => this.data.sides[key] || null);
      const selectedNames = keys.map(
        (key, index) => sideProfiles[index]?.label || key.replace(/-/g, ' ')
      );
      const additiveProfiles = [...componentProfiles, ...sideProfiles];
      const requiredSides = Number(this.dataset.requiredSides || 0);
      this.updateViewTabs();

      const servingSize = this.querySelector('[data-nutrition-serving-size]');
      if (servingSize) {
        const baseServingSize = base.serving_size || '1 meal';
        const servingParts = [baseServingSize];
        const servingSuffix = String(nutritionView?.serving_suffix || '').trim();

        if (servingSuffix) {
          servingParts.push(servingSuffix);
        } else if (componentNames.length) {
          servingParts.push(...componentNames);
        }
        if (keys.length) {
          servingParts.push(`${keys.length} ${keys.length === 1 ? 'side' : 'sides'}`);
        }

        servingSize.textContent = servingParts.join(' + ');
      }

      const badge = this.querySelector('[data-nutrition-badge]');
      if (badge) {
        if (requiredSides > 0 && keys.length >= requiredSides) {
          badge.textContent = 'Full meal';
        } else if (keys.length) {
          badge.textContent = `${keys.length} of ${requiredSides} sides`;
        } else if (requiredSides > 0) {
          badge.textContent = 'Base dish';
        } else if (nutritionView) {
          badge.textContent = nutritionView.badge || nutritionView.label || 'Complete meal';
        } else {
          badge.textContent = 'Base dish';
        }
      }

      const calories = this.totalFor('calories', additiveProfiles);
      this.querySelectorAll('[data-nutrition-calories]').forEach((calorieTarget) => {
        calorieTarget.textContent = formatAmount(calories);
      });

      const totals = Object.fromEntries(
        NUTRIENTS.map((key) => [key, this.totalFor(key, additiveProfiles)])
      );
      NUTRIENTS.forEach((key) => this.updateAmount(key, totals[key]));

      const lifestyleResult = this.lifestyleForBuild({
        requiredSides,
        keys,
        totalCarbs: totals.total_carbohydrate_g,
        dietaryFiber: totals.dietary_fiber_g,
      });
      this.renderLifestyle(lifestyleResult);

      // Nutrition tabs are informational. The ordered build does not change when a
      // shopper views an alternate panel, so cart snapshots always use the
      // configured default view (the complete meal for the bisque).
      const cartViewId = this.resolveNutritionViewId(this.data.nutritionViews?.default);
      const cartView = this.nutritionViewItems.find((view) => String(view.id) === cartViewId) || nutritionView;
      const cartComponentProfiles = this.componentEntriesForView(cartView).map((entry) => entry.profile);
      const cartAdditiveProfiles = [...cartComponentProfiles, ...sideProfiles];
      const cartCalories = this.totalFor('calories', cartAdditiveProfiles);
      const cartTotals = Object.fromEntries(
        NUTRIENTS.map((key) => [key, this.totalFor(key, cartAdditiveProfiles)])
      );
      const cartLifestyleResult = this.lifestyleForBuild({
        requiredSides,
        keys,
        totalCarbs: cartTotals.total_carbohydrate_g,
        dietaryFiber: cartTotals.dietary_fiber_g,
      });
      this.updateCartSnapshot('calories', cartCalories);
      this.updateCartSnapshot('total_fat_g', cartTotals.total_fat_g);
      this.updateCartSnapshot('total_carbohydrate_g', cartTotals.total_carbohydrate_g);
      this.updateCartSnapshot('protein_g', cartTotals.protein_g);
      this.updateCartSnapshot('lifestyle', cartLifestyleResult.finalLabels.join(', '));
      const cartScope = [base.serving_size || 'Base dish',
        ...this.componentEntriesForView(cartView).map(entry => entry.label), ...selectedNames].join(' + ');
      this.updateCartSnapshot('scope', cartScope + '. Optional extras excluded.');

      const context = this.querySelector('[data-nutrition-context]');
      if (context) {
        if (nutritionView) {
          const contextParts = [];
          const viewContext = String(nutritionView.context || '').trim();

          if (viewContext) {
            contextParts.push(viewContext);
          } else if (componentNames.length) {
            contextParts.push(`Nutrition for the base dish with ${componentNames.join(' + ')}.`);
          } else {
            contextParts.push('Nutrition for the base dish only.');
          }

          if (keys.length) {
            contextParts.push(`Selected ${keys.length === 1 ? 'side' : 'sides'}: ${selectedNames.join(' + ')}.`);
          } else if (requiredSides > 0) {
            contextParts.push(`Choose ${requiredSides === 1 ? 'your side' : 'both sides'} to see the full meal total.`);
          }

          context.textContent = contextParts.join(' ');
        } else if (keys.length) {
          context.textContent = `Meal total: base dish + ${selectedNames.join(' + ')}.`;
        } else if (requiredSides > 0) {
          context.textContent = `Base dish shown. Choose ${requiredSides === 1 ? 'your side' : 'both sides'} in the chooser to see the full meal total.`;
        } else {
          context.textContent = 'Nutrition for this complete dish.';
        }
      }

      const source = this.querySelector('[data-nutrition-source]');
      if (source) {
        const finalized = [base, ...additiveProfiles].every(profile => profile?.status === 'finalized');
        source.hidden = false;
        source.textContent = finalized
          ? 'Finalized nutrition for the portion and components shown.'
          : 'Estimated nutrition based on recipe and portion data; actual values may vary. Not a finalized label.';
        const certainty = this.querySelector('[data-nutrition-certainty]');
        if (certainty) certainty.textContent = finalized ? 'Nutrition Facts' : 'Estimated nutrition';
      }

      const additiveEntries = [
        ...componentEntries,
        ...keys.map((key, index) => ({ key, profile: sideProfiles[index], label: selectedNames[index] })),
      ];
      const incompleteAdditions = [
        ...new Set(
          additiveEntries
            .filter(
              ({ profile }) =>
                !profile || CORE_ADDITIVE_NUTRIENTS.some((key) => toNumber(profile[key]) === null)
            )
            .map(({ label }) => label)
        ),
      ];
      const warning = this.querySelector('[data-nutrition-warning]');
      if (warning) {
        warning.hidden = incompleteAdditions.length === 0;
        warning.textContent = incompleteAdditions.length === 0
          ? ''
          : Number.isFinite(calories)
            ? `Calories include your selection. Some nutrient values are not yet available for ${incompleteAdditions.join(' and ')}.`
            : `Complete nutrition is not yet available for ${incompleteAdditions.join(' and ')}.`;
      }

      if (announce) {
        const live = this.querySelector('[data-nutrition-live]');
        if (live) {
          const viewLabel = String(nutritionView?.label || '').trim();
          const viewDescription = viewLabel ? ` for ${viewLabel}` : '';
          const sideDescription = selectedNames.length
            ? `${viewLabel ? ' with' : ' for the base dish plus'} ${selectedNames.join(' and ')}`
            : '';
          live.textContent = Number.isFinite(calories)
            ? `Nutrition updated to ${formatAmount(calories)} calories${viewDescription}${sideDescription}.`
            : 'Nutrition updated. Some totals are not yet available.';
        }
      }

      this.dispatchEvent(
        new CustomEvent('purefect:nutrition-updated', {
          bubbles: true,
          detail: {
            calories,
            fat: totals.total_fat_g,
            carbs: totals.total_carbohydrate_g,
            protein: totals.protein_g,
            lifestyle: lifestyleResult.finalLabels,
            complete: lifestyleResult.selectionComplete,
            nutritionView: nutritionView
              ? {
                  id: String(nutritionView.id),
                  label: String(nutritionView.label || nutritionView.id),
                  componentKeys: componentEntries.map((entry) => entry.key),
                }
              : null,
          },
        })
      );
    }
  }

  customElements.define('purefect-nutrition-facts', PurefectNutritionFacts);
})();
